import React from 'react';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';
import { layout } from '../../theme';

const COLLAPSE_KEY = 'agentx.sidebar.collapsed';

/**
 * Chrome around every authenticated page: fixed topbar, sidebar, and a single
 * scrolling content column with a capped measure so tables stay readable on
 * ultrawide displays.
 */
export const AppShell: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('md'));

  const [collapsed, setCollapsed] = React.useState(() => {
    try {
      return localStorage.getItem(COLLAPSE_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [mobileOpen, setMobileOpen] = React.useState(false);

  const toggleCollapse = () => {
    setCollapsed((prev) => {
      try {
        localStorage.setItem(COLLAPSE_KEY, prev ? '0' : '1');
      } catch {
        /* private mode — the preference just does not persist */
      }
      return !prev;
    });
  };

  // Route changes on mobile should not leave the overlay drawer covering the page.
  React.useEffect(() => {
    if (!mobile) setMobileOpen(false);
  }, [mobile]);

  const sidebarWidth = mobile ? 0 : collapsed ? layout.sidebarRailWidth : layout.sidebarWidth;

  return (
    <Box sx={{ display: 'flex', minHeight: '100dvh', bgcolor: 'background.default' }}>
      <Topbar mobile={mobile} sidebarWidth={sidebarWidth} onOpenSidebar={() => setMobileOpen(true)} />
      <Sidebar
        open={mobile ? mobileOpen : !collapsed}
        mobile={mobile}
        onClose={() => setMobileOpen(false)}
        onToggleCollapse={toggleCollapse}
      />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          pt: `${layout.topbarHeight}px`,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 1520, mx: 'auto', px: { xs: 2, sm: 3, lg: 4 }, py: { xs: 2.5, sm: 3.5 } }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default AppShell;
